import { parseISO } from 'date-fns'
import {
  Archive,
  ArchiveRestore,
  Boxes,
  Clock,
  EllipsisVertical,
  Package,
  PackagePlusIcon,
  Pencil,
  Upload,
  X,
} from 'lucide-react'
import {
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
} from 'nuqs'
import { useState } from 'react'
import { generatePath, Link } from 'react-router-dom'

import type {
  ClinicId,
  ProductCategory,
  ProductType,
  ProductWithStock,
  Stock,
} from '@snootsvet/shared'
import {
  clinicIdSchema,
  formatQuantity,
  formatRegionPrice,
  getClinics,
  productCategorySchema,
  productTypeSchema,
} from '@snootsvet/shared'

import { ClinicSearchWithDefault } from '@/frontend/components/clinic-search'
import { Pagination } from '@/frontend/components/pagination'
import { SearchInput } from '@/frontend/components/search-input'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/frontend/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/frontend/components/ui/tooltip'
import { ProductForm } from '@/frontend/features/inventory/product.form'
import { ReceiveStockForm } from '@/frontend/features/inventory/receive-stock.form'
import { useListProductsWithStock } from '@/frontend/hooks/api/use-list-products-with-stock'
import { usePatchProduct } from '@/frontend/hooks/api/use-patch-product'
import { useDebounce } from '@/frontend/hooks/use-debounce'
import { useDocumentTitle } from '@/frontend/hooks/use-document-title'
import { routes } from '@/frontend/routes'

const PAGE_SIZE = 50

/** Stock below this threshold shows as "Low Stock". */
const EXPIRY_WARNING_DAYS = 7

/** Inventory management page — product catalog with stock levels. */
export function InventoryPage() {
  useDocumentTitle('Inventory')
  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [receiveStockDialogOpen, setReceiveStockDialogOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<ProductWithStock | null>(null)

  // Clinic filter — seed from user settings on first load
  const [clinicFilter, setClinicFilter] = useQueryState(
    'clinic',
    parseAsStringLiteral(clinicIdSchema.options)
  )
  // Full clinic list — used only to resolve clinic names for the stock table
  // (a lookup, not a picker). The clinic *filter* below is a ClinicSearchWithDefault,
  // which is already region-scoped and clears an out-of-region selection itself.
  const clinics = getClinics({ includeArchived: true })

  // Search & other filters
  const [search, setSearch] = useQueryState('q', parseAsString)
  const [categoryFilter, setCategoryFilter] = useQueryState('category', parseAsString)
  const [typeFilter, setTypeFilter] = useQueryState('type', parseAsString)
  const [page, setPage] = useQueryState('page', parseAsInteger)
  const [showArchived, setShowArchived] = useQueryState('archived', parseAsBoolean)

  const debouncedSearch = useDebounce(search ?? '', 300)
  const q = debouncedSearch.length >= 2 ? debouncedSearch : undefined
  const currentPage = page ?? 1
  const offset = (currentPage - 1) * PAGE_SIZE

  // Product data
  const { data, isLoading, error } = useListProductsWithStock({
    q,
    active: showArchived ? undefined : true,
    category: (categoryFilter as ProductCategory) ?? undefined,
    type: typeFilter ? [typeFilter as ProductType] : undefined,
    limit: PAGE_SIZE,
    offset,
  })
  const products = data?.products
  const totalPages = data ? Math.ceil(data.totalCount / PAGE_SIZE) : 0

  /** Reset to page 1 when any filter changes. */
  function handleSearchChange(value: string) {
    setSearch(value || null)
    setPage(null)
  }
  function handleCategoryChange(value: string | null) {
    setCategoryFilter(value || null)
    setPage(null)
  }
  function handleTypeChange(value: string | null) {
    setTypeFilter(value || null)
    setPage(null)
  }

  if (error) return <div>Error: {error.message}</div>

  return (
    <div>
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-headline-lg">Inventory</h1>
        <div className="flex items-center gap-2">
          <Button size="lg" onClick={() => setAddDialogOpen(true)}>
            <PackagePlusIcon />
            Add Product
          </Button>
          <Button size="lg" variant="outline" onClick={() => setReceiveStockDialogOpen(true)}>
            <Package />
            Receive Stock
          </Button>
          <Button size="lg" variant="outline" disabled>
            <Upload className="size-4" />
            Upload Spreadsheet
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex items-center gap-3">
        <ClinicSearchWithDefault
          includeArchived
          id="filter-clinic"
          value={clinicFilter}
          onChange={setClinicFilter}
          placeholder="All Clinics"
          allowClear
        />
        <SearchInput
          value={search ?? ''}
          onChange={handleSearchChange}
          placeholder="Search products..."
        />
        <Select
          value={categoryFilter ?? ''}
          onValueChange={handleCategoryChange}
          items={[
            { label: 'All Categories', value: '' },
            ...productCategorySchema.options.map((cat) => ({ label: cat, value: cat })),
          ]}
        >
          <SelectTrigger className="w-64">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">All Categories</SelectItem>
            {productCategorySchema.options.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={typeFilter ?? ''}
          onValueChange={handleTypeChange}
          items={[
            { label: 'All Types', value: '' },
            ...productTypeSchema.options.map((type) => ({
              label: type.charAt(0).toUpperCase() + type.slice(1),
              value: type,
            })),
          ]}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">All Types</SelectItem>
            {productTypeSchema.options.map((type) => (
              <SelectItem key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label
          htmlFor="show-archived"
          className="flex items-center gap-2 text-muted-foreground cursor-pointer select-none"
        >
          <Checkbox
            id="show-archived"
            checked={showArchived ?? false}
            onCheckedChange={(checked) => {
              setShowArchived(checked ? true : null)
              setPage(null)
            }}
          />
          Show archived
        </label>
        {(search || clinicFilter || categoryFilter || typeFilter || showArchived) && (
          <Button
            variant="ghost"
            onClick={() => {
              setSearch(null)
              setClinicFilter(null)
              setCategoryFilter(null)
              setTypeFilter(null)
              setShowArchived(null)
              setPage(null)
            }}
          >
            <X className="size-4" />
            Clear filters
          </Button>
        )}
      </div>

      {/* Product table */}
      {isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : !products?.length ? (
        <p className="py-8 text-center text-muted-foreground">No products found.</p>
      ) : (
        <>
          <ProductTable
            products={products}
            clinicFilter={clinicFilter}
            clinics={clinics ?? []}
            onEdit={setEditingProduct}
          />
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onPageChange={(p) => setPage(p === 1 ? null : p)}
          />
        </>
      )}

      {/* Receive Stock dialog */}
      <Dialog open={receiveStockDialogOpen} onOpenChange={setReceiveStockDialogOpen}>
        <DialogContent className="sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle>Receive Stock</DialogTitle>
          </DialogHeader>
          <ReceiveStockForm
            defaultClinicId={clinicFilter}
            onSuccess={() => setReceiveStockDialogOpen(false)}
            onCancel={() => setReceiveStockDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Add Product dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Add Product</DialogTitle>
          </DialogHeader>
          <ProductForm
            onSuccess={() => setAddDialogOpen(false)}
            onCancel={() => setAddDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Product dialog */}
      <Dialog
        open={editingProduct !== null}
        onOpenChange={(open) => {
          if (!open) setEditingProduct(null)
        }}
      >
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
          </DialogHeader>
          {editingProduct && (
            <ProductForm
              productId={editingProduct.id}
              defaultValues={{
                name: editingProduct.name,
                sku: editingProduct.sku,
                type: editingProduct.type,
                unit: editingProduct.unit,
                packSize: editingProduct.packSize,
                packUnit: editingProduct.packUnit,
                category: editingProduct.category,
                packCost: editingProduct.packCost,
                packFixedPrice: editingProduct.packFixedPrice,
                markupBps: editingProduct.markupBps,
                vatBps: editingProduct.vatBps,
                manufacturer: editingProduct.manufacturer ?? '',
                isControlledSubstance: editingProduct.isControlledSubstance,
                fleaWormTick: editingProduct.fleaWormTick,
                lowStockThreshold: editingProduct.lowStockThreshold,
              }}
              onSuccess={() => setEditingProduct(null)}
              onCancel={() => setEditingProduct(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Product table
// ---------------------------------------------------------------------------

function ProductTable({
  products,
  clinicFilter,
  clinics,
  onEdit,
}: {
  products: ProductWithStock[]
  clinicFilter: ClinicId | null
  clinics: { id: string; name: string }[]
  onEdit: (product: ProductWithStock) => void
}) {
  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[19%]">Product Name</TableHead>
          <TableHead className="w-[11%]">Subunit/Unit</TableHead>
          <TableHead className="w-[11%]">Category</TableHead>
          <TableHead className="w-[11%]">Supplier</TableHead>
          <TableHead className="w-[8%] text-right">COGS</TableHead>
          <TableHead className="w-[8%] text-right">Fixed Price</TableHead>
          <TableHead className="w-[7%] text-right">Markup</TableHead>
          <TableHead className="w-[8%] text-right">Stock</TableHead>
          <TableHead className="w-[11%] text-center">Status</TableHead>
          <TableHead className="w-[5%]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {products.map((product) => (
          <ProductRow
            key={product.id}
            product={product}
            clinicFilter={clinicFilter}
            clinics={clinics}
            onEdit={() => onEdit(product)}
          />
        ))}
      </TableBody>
    </Table>
  )
}

// ---------------------------------------------------------------------------
// Product row
// ---------------------------------------------------------------------------

function ProductRow({
  product,
  clinicFilter,
  clinics,
  onEdit,
}: {
  product: ProductWithStock
  clinicFilter: ClinicId | null
  clinics: { id: string; name: string }[]
  onEdit: () => void
}) {
  const { mutate: patchProduct, isPending: isPatching } = usePatchProduct()

  // Filter stock batches to selected clinic, or show all
  const filteredStock = clinicFilter
    ? product.stock.filter((s) => s.clinicId === clinicFilter)
    : product.stock

  const totalAvailable = filteredStock.reduce((sum, s) => sum + s.quantity, 0)

  // Sum quantity per clinic for the per-clinic breakdown
  const stockByClinic = Map.groupBy(filteredStock, (s) => s.clinicId)

  const clinicNames = new Map(clinics.map((c) => [c.id, c.name]))

  const rowBorderClass =
    !product.isActive || product.type === 'service' || totalAvailable > product.lowStockThreshold
      ? 'border-l-4 border-l-transparent'
      : totalAvailable <= 0
        ? 'border-l-4 border-l-destructive-fg'
        : 'border-l-4 border-l-orange'

  return (
    <TableRow className={rowBorderClass}>
      <TableCell className="font-medium whitespace-normal">
        <Link
          to={`${generatePath(routes.productStock, { productId: product.id })}${
            clinicFilter ? `?clinic=${clinicFilter}` : ''
          }`}
          className="hover:underline"
        >
          {product.name}
        </Link>
      </TableCell>
      <TableCell className="text-muted-foreground whitespace-normal">
        {product.type !== 'service' ? product.packLabel : '—'}
      </TableCell>
      <TableCell className="truncate capitalize text-muted-foreground" title={product.category}>
        {product.category}
      </TableCell>
      <TableCell className="text-muted-foreground whitespace-normal">
        {product.supplier ?? '—'}
      </TableCell>
      <TableCell className="text-right text-muted-foreground whitespace-nowrap">
        {formatRegionPrice(product.packCost, product.region)}
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {product.packFixedPrice !== null
          ? formatRegionPrice(product.packFixedPrice, product.region)
          : '—'}
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {product.markupBps !== null ? `${(product.markupBps / 100).toFixed(1)}%` : '—'}
      </TableCell>
      <TableCell className="text-right">
        <div>
          {clinicFilter && filteredStock.length > 0 ? (
            <BatchStockOverlay batches={filteredStock}>
              <span className="font-semibold underline decoration-dotted underline-offset-2">
                {formatQuantity(totalAvailable)}
              </span>
            </BatchStockOverlay>
          ) : (
            <span className="font-semibold">{formatQuantity(totalAvailable)}</span>
          )}
          {!clinicFilter && stockByClinic.size > 0 && (
            <div className="mt-0.5 space-y-px">
              {[...stockByClinic.entries()].map(([clinicId, batches]) => (
                <BatchStockOverlay
                  key={clinicId}
                  label={clinicNames.get(clinicId) ?? clinicId}
                  batches={batches}
                />
              ))}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className="text-center">
        <div className="flex flex-col items-center gap-1">
          {!product.isActive ? (
            <Badge size="sm" className="bg-muted text-muted-foreground">
              Archived
            </Badge>
          ) : (
            <>
              <StockStatusBadge
                available={totalAvailable}
                type={product.type}
                threshold={product.lowStockThreshold}
              />
              {filteredStock.some(isBatchExpiringSoon) && (
                <Badge size="sm" variant="warning">
                  <Clock />
                  Expiring Soon
                </Badge>
              )}
            </>
          )}
        </div>
      </TableCell>
      <TableCell className="text-center">
        <div className="flex items-center justify-center">
          <DropdownMenu>
            <DropdownMenuTrigger
              render={<Button variant="ghost" size="icon" aria-label="Product actions" />}
            >
              <EllipsisVertical className="size-4 text-muted-foreground" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="bottom">
              <DropdownMenuItem
                render={
                  <Link
                    to={`${generatePath(routes.productStock, { productId: product.id })}${
                      clinicFilter ? `?clinic=${clinicFilter}` : ''
                    }`}
                  />
                }
              >
                <Boxes className="size-3.5" />
                Manage stock
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="size-3.5" />
                Edit product
              </DropdownMenuItem>
              {product.isActive ? (
                <DropdownMenuItem
                  variant="destructive"
                  disabled={isPatching}
                  onClick={() => patchProduct({ id: product.id, isActive: false })}
                >
                  <Archive className="size-3.5" />
                  Archive product
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  disabled={isPatching}
                  className="text-success-fg focus:text-success-fg"
                  onClick={() => patchProduct({ id: product.id, isActive: true })}
                >
                  <ArchiveRestore className="size-3.5" />
                  Unarchive product
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  )
}

// ---------------------------------------------------------------------------
// Stock status badge
// ---------------------------------------------------------------------------

/** Renders a stock status badge based on available quantity. Services are always "N/A". */
function StockStatusBadge({
  available,
  type,
  threshold,
}: {
  available: number
  type: string
  threshold: number
}) {
  if (type === 'service') {
    return (
      <Badge size="sm" className="bg-muted text-muted-foreground">
        N/A
      </Badge>
    )
  }

  if (available <= 0) {
    return (
      <Badge size="sm" variant="destructive">
        OOS
      </Badge>
    )
  }

  if (available < threshold) {
    return (
      <Badge size="sm" variant="warning">
        Low
      </Badge>
    )
  }

  return (
    <Badge size="sm" variant="success">
      In Stock
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Batch stock overlay
// ---------------------------------------------------------------------------

/** Returns true if a stock batch expires within the warning threshold. */
function isBatchExpiringSoon(batch: Stock): boolean {
  if (!batch.expiryDate) return false
  const expiry = new Date(batch.expiryDate).getTime()
  const now = Date.now()
  return expiry > now && expiry <= now + EXPIRY_WARNING_DAYS * 24 * 60 * 60 * 1000
}

/**
 * Wraps its trigger in a tooltip showing a per-batch breakdown on hover.
 * When `children` are provided they become the trigger; otherwise renders
 * a per-clinic row with the label and summed total.
 */
function BatchStockOverlay({
  label,
  batches,
  children,
}: {
  label?: string
  batches: Stock[]
  children?: React.ReactNode
}) {
  const total = batches.reduce((sum, b) => sum + b.quantity, 0)

  return (
    <TooltipProvider delay={300}>
      <Tooltip>
        {children ? (
          <TooltipTrigger render={<span />} className="cursor-default">
            {children}
          </TooltipTrigger>
        ) : (
          <TooltipTrigger
            render={<div />}
            className="flex items-center justify-end gap-1 text-sm text-muted-foreground cursor-default"
          >
            <span className="truncate max-w-32 underline decoration-dotted underline-offset-2">
              {label}
            </span>
            <span className="tabular-nums">{formatQuantity(total)}</span>
          </TooltipTrigger>
        )}
        <TooltipContent
          variant="unstyled"
          side="top"
          className="rounded-md border bg-popover px-3 py-2 text-sm text-popover-foreground shadow-md space-y-1"
        >
          {batches.map((b) => {
            const expiring = isBatchExpiringSoon(b)
            return (
              <div key={b.id} className="flex items-center gap-3">
                {b.batchNumber ? (
                  <span className="font-mono text-foreground">{b.batchNumber}</span>
                ) : null}
                <span className="tabular-nums">{formatQuantity(b.quantity)} units</span>
                {b.expiryDate && (
                  <span
                    className={
                      expiring
                        ? 'text-warning-accent flex items-center gap-1'
                        : 'text-muted-foreground'
                    }
                  >
                    {expiring && <Clock className="size-3" />}
                    exp.{' '}
                    {parseISO(b.expiryDate).toLocaleDateString('en-GB', {
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                )}
              </div>
            )
          })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
